import { useState, useEffect, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/utils';
import { pushFirmware } from '../../api';
import type { TransmitterV2Document, FirmwareOption } from '../../types';

const DEFAULT_MAX_OTA_ATTEMPTS = 3;
const DEFAULT_OTA_DELAY_MINUTES = 15;

// =============================================================================
// Props
// =============================================================================

interface FirmwareOtaSectionProps {
  transmitter: TransmitterV2Document;
  firmwares: FirmwareOption[];
  onRefresh: () => void;
}

// =============================================================================
// Helpers
// =============================================================================

function formatDateOrDash(dateString: string | null | undefined): string {
  if (!dateString) return '--';
  return formatDateTime(dateString);
}

// =============================================================================
// Component
// =============================================================================

export function FirmwareOtaSection({
  transmitter,
  firmwares,
  onRefresh,
}: FirmwareOtaSectionProps) {
  // Local state
  const [selectedFirmware, setSelectedFirmware] = useState<string>(
    transmitter.firmware || '',
  );
  const [maxAttempts, setMaxAttempts] = useState<number>(
    transmitter.max_ota_attempts ?? DEFAULT_MAX_OTA_ATTEMPTS,
  );
  const [retryDelay, setRetryDelay] = useState<number>(
    transmitter.ota_delay_minutes ?? DEFAULT_OTA_DELAY_MINUTES,
  );
  const [showTestVersions, setShowTestVersions] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const isLoRaWAN = Boolean(transmitter.downlink_id);

  // Re-sync local state when transmitter prop changes (e.g. after refresh)
  useEffect(() => {
    setSelectedFirmware(transmitter.firmware || '');
    setMaxAttempts(transmitter.max_ota_attempts ?? DEFAULT_MAX_OTA_ATTEMPTS);
    setRetryDelay(transmitter.ota_delay_minutes ?? DEFAULT_OTA_DELAY_MINUTES);
  }, [transmitter]);

  // Filter firmwares: stable only by default, all when toggle is on
  const filteredFirmwares = useMemo(() => {
    const activeFirmwares = firmwares.filter((fw) => fw.status !== 'archive');
    if (showTestVersions) return activeFirmwares;
    return activeFirmwares.filter((fw) => fw.status === 'stable');
  }, [firmwares, showTestVersions]);

  // Push firmware handler
  const handlePushFirmware = async () => {
    if (isLoRaWAN) {
      toast.error('Firmware OTA is not available for LoRaWAN devices');
      return;
    }

    if (!selectedFirmware) {
      toast.error('Please select a firmware version');
      return;
    }

    setIsPushing(true);
    try {
      await pushFirmware(
        transmitter._id,
        {
          firmware: selectedFirmware,
          max_ota_attempts: maxAttempts,
          ota_delay_minutes: retryDelay,
          to_update: true,
          ota_firmware_attempts: 0,
          last_ota_attempt: null,
        },
      );
      toast.success('Firmware push initiated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to push firmware', {
        description:
          error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsPushing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Firmware OTA
      </h3>

      {isLoRaWAN && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          Firmware OTA is not available for LoRaWAN devices.
        </div>
      )}

      {/* Desired Firmware */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Desired Firmware</Label>
        <div className="flex items-center gap-2">
          <Select value={selectedFirmware} onValueChange={setSelectedFirmware} disabled={isLoRaWAN}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select firmware..." />
            </SelectTrigger>
            <SelectContent>
              {filteredFirmwares.map((fw) => (
                <SelectItem key={fw._id} value={fw._id}>
                  {fw.resolved_name || fw._id}
                  {fw.status && fw.status !== 'stable' && (
                    <span className="ml-1 text-muted-foreground">
                      ({fw.status})
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onRefresh}
            title="Refresh firmware list"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Show Test Versions toggle */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label htmlFor="show-test-versions">Test Versions</Label>
        <div className="flex items-center gap-2">
          <Switch
            id="show-test-versions"
            checked={showTestVersions}
            onCheckedChange={setShowTestVersions}
          />
          <span className="text-sm text-muted-foreground">
            {showTestVersions ? 'Showing all versions' : 'Stable only'}
          </span>
        </div>
      </div>

      {/* Current Firmware */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Current Firmware</Label>
        <span className="text-sm">
          {transmitter.current_firmware_name || '--'}
        </span>
      </div>

      {/* OTA Update Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Attempts</TableHead>
              <TableHead>Last Attempt</TableHead>
              <TableHead>Max Attempts</TableHead>
              <TableHead>Retry Delay (min)</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="text-sm">
                {transmitter.ota_firmware_attempts ?? 0}
              </TableCell>
              <TableCell className="text-sm">
                {formatDateOrDash(transmitter.last_ota_attempt)}
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={maxAttempts}
                  onChange={(e) =>
                    setMaxAttempts(Number(e.target.value) || DEFAULT_MAX_OTA_ATTEMPTS)
                  }
                  className="w-20 h-8"
                  disabled={isLoRaWAN}
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  min={1}
                  max={1440}
                  value={retryDelay}
                  onChange={(e) =>
                    setRetryDelay(Number(e.target.value) || DEFAULT_OTA_DELAY_MINUTES)
                  }
                  className="w-20 h-8"
                  disabled={isLoRaWAN}
                />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  type="button"
                  size="sm"
                  onClick={handlePushFirmware}
                  disabled={isLoRaWAN || !selectedFirmware || isPushing}
                  title={isLoRaWAN ? 'Firmware OTA is not available for LoRaWAN devices' : undefined}
                >
                  {isPushing ? 'Pushing...' : 'Push Firmware'}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Pending OTA indicator */}
      {transmitter.to_update && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
          Firmware update pending — transmitter will update on next check-in.
        </div>
      )}

      {/* Firmware History */}
      {transmitter.firmware_changes && transmitter.firmware_changes.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">
            Firmware History
          </Label>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Firmware</TableHead>
                  <TableHead>Version</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transmitter.firmware_changes.map((change, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-sm">
                      {formatDateOrDash(change.date)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {change.firmware?.firmwaredistribution?.name || '--'}
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {change.firmware?.version || '--'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
